/**
 * UI / app side effects for HTTP errors (toasts, alerts, navigation, Redux).
 * Kept separate from transport (axios/fetch) in APIRequest so layers stay distinct.
 */
import { clearLoading } from '../redux/actions/noti';
import { getReduxStore } from '../redux/reduxStoreBridge';
import { AlertCustom } from './alert';
import { showErrorToast, GLOBAL_HTTP_500_TOAST_ID } from '@/lib/toast';
import {
  goToNotFoundPage,
  navigateTo,
} from '@/application/helpers/navigationBridge';
import { getRouteIndexPathFor500 } from '@/application/helpers/routeIndexOnServerError';
import { isForceClearLogout } from '@/application/helpers/storage';
import { SESSION_KEYS } from './storageKeys';
import i18next from 'i18next';

const dispatchClearLoading = () => {
  getReduxStore()?.dispatch(clearLoading());
};

const tWithFallback = (key: string, fallback: string) => {
  const translated = i18next.t(key);
  return translated === key ? fallback : translated;
};

export const notifyTokenBlacklisted = () => {
  dispatchClearLoading();
  const title = tWithFallback('AUTH.SESSION_REVOKED_TITLE', 'Session revoked');
  const toastMessage = tWithFallback(
    'AUTH.SESSION_REVOKED_MESSAGE',
    'Your session has been revoked. Please login again.'
  );

  sessionStorage.setItem(
    SESSION_KEYS.AUTH_LOGOUT_TOAST,
    JSON.stringify({ title, message: toastMessage })
  );
  isForceClearLogout();
};

export const runNotFoundSideEffects = () => {
  dispatchClearLoading();
  goToNotFoundPage();
};

/** GET/HEAD/OPTIONS: do not redirect to parent index on 500 (e.g. edit page load failing). */
const isReadHttpMethod = (method?: string) => {
  const m = (method || 'GET').toUpperCase();
  return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
};

export const runServerErrorSideEffects = (args: {
  error: { response?: { data?: { message?: string } } };
  originalRequest: { method?: string; skipGlobal500ErrorToast?: boolean } | undefined;
}) => {
  const { error, originalRequest } = args;
  dispatchClearLoading();
  const skipGlobal500Toast = Boolean(
    originalRequest?.skipGlobal500ErrorToast
  );
  if (!skipGlobal500Toast) {
    const serverMessage = error.response?.data?.message;
    showErrorToast({
      title: i18next.t('FAILED'),
      message:
        serverMessage || i18next.t('GAME_LIST.NOTIFICATION.UPDATE_FAILED'),
      toastId: GLOBAL_HTTP_500_TOAST_ID,
    });
    const reqMethod = originalRequest?.method as string | undefined;
    if (!isReadHttpMethod(reqMethod) && typeof window !== 'undefined') {
      const indexPath = getRouteIndexPathFor500(window.location.pathname);
      if (indexPath) {
        navigateTo(indexPath, { replace: true });
      }
    }
  }
};

/**
 * Handles API error responses and shows appropriate alerts.
 * @param httpStatus - HTTP status code
 * @param res - Response object with message/code
 * @param requestMethod - HTTP method (for 500: skip index redirect on GET/HEAD/OPTIONS)
 * @returns Processed response or special marker for 401 refresh
 */
export function handleError(httpStatus: any, res: any, requestMethod?: string) {
  if (
    httpStatus === 403 &&
    (res?.code === 'TOKEN_BLACKLISTED' ||
      (typeof res === 'string' && res.trim().toLowerCase() === 'forbidden'))
  ) {
    notifyTokenBlacklisted();
    return res;
  }

  if (httpStatus === 404) {
    runNotFoundSideEffects();
    return res;
  }

  switch (httpStatus) {
    case 400:
    case 503:
      dispatchClearLoading();
      AlertCustom({
        type: 'error',
        title: 'ERROR',
        message: res.message,
        showBtnCancel: false,
      });
      break;

    case 401:
      return { _shouldRefreshToken: true, originalResponse: res };

    case 500:
      dispatchClearLoading();
      showErrorToast({
        title: i18next.t('FAILED'),
        message:
          res?.message || i18next.t('GAME_LIST.NOTIFICATION.UPDATE_FAILED'),
        toastId: GLOBAL_HTTP_500_TOAST_ID,
      });
      if (!isReadHttpMethod(requestMethod) && typeof window !== 'undefined') {
        const indexPath = getRouteIndexPathFor500(window.location.pathname);
        if (indexPath) {
          navigateTo(indexPath, { replace: true });
        }
      }
      break;

    default:
      switch (res?.code) {
        case 0:
          return res;

        case 1309:
        case 2300:
        case 8017:
        case 4102:
        case 9902:
          AlertCustom({ type: 'error', message: 'res.message 999' }).then(
            (result) => {
              if (result.isConfirmed) {
                isForceClearLogout();
              }
            }
          );
          return res;

        default:
          dispatchClearLoading();
          AlertCustom({
            type: 'error',
            title: 'ERROR',
            message: res.message,
            showBtnCancel: false,
          });
          return res;
      }
  }
  return res;
}

export { isReadHttpMethod };
